'use client'

import React from 'react'
import { Globe, Plus, ChevronDown } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import useChains from 'state/chain/hooks'
import { cn } from 'lib/utils'
import { isEquityDeal } from 'views/ProjectsV2/Shortlist/equity'
import { useUserRoles } from 'state/user/hooks'
import { Button } from '../ui/button'
import { Card, CardContent } from '../ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { TimelineSection } from './timeline-section'
import useEquitySubmissions from 'hooks/useEquitySubmissions'

interface ParticipantRow {
  id: number
  legalName: string
  privyId: string
  registeredAddress: string
  walletAddress: string
  email: string
}

export function PostSubmissionSummary({
  project,
  showTgeDateModal,
  setShowTgeDateModal,
  onTgeDateUpdate,
  isSavingTge,
  tgeError,
}: {
  project: any
  showTgeDateModal?: boolean
  setShowTgeDateModal?: (show: boolean) => void
  onTgeDateUpdate?: (tgeDate: string) => void
  isSavingTge?: boolean
  tgeError?: string | null
}) {
  const [showFullSubmission, setShowFullSubmission] = useState(false)
  const [participantsOpen, setParticipantsOpen] = useState(false)
  // const [copiedLink, setCopiedLink] = useState(false)
  // const [copiedSale, setCopiedSale] = useState(false)
  // const [copiedAnalytics, setCopiedAnalytics] = useState(false)
  const isCurator = useUserRoles().includes('CURATOR')
  const chains = useChains()

  const equityId =
    project?.id !== undefined && project?.id !== null
      ? String(project.id)
      : undefined
  const {
    submissions: equitySubmissionRows,
    isLoading: isLoadingEquitySubmissions,
    isError: equitySubmissionsError,
  } = useEquitySubmissions(equityId)

  const participantsList: ParticipantRow[] = React.useMemo(
    () =>
      equitySubmissionRows.map((submission: any) => ({
        id: submission.id,
        legalName: submission.data?.name ?? '',
        privyId: submission.privy_id,
        registeredAddress: submission.data?.registeredAddress ?? '',
        walletAddress: submission.data?.walletAddress ?? '',
        email: submission.email ?? '',
      })),
    [equitySubmissionRows],
  )
  const chain =
    chains.find((chain) => chain.chainID === project.depositChainId) ?? null
  const [selectedDateTime, setSelectedDateTime] = useState('')
  const [localTgeError, setLocalTgeError] = useState<string | null>(null)
  const depositEndTimeline =
    project?.timelines?.find((entry: any) => entry?.type === 'depositEnds') ??
    null
  const depositEndDate = depositEndTimeline
    ? new Date(depositEndTimeline.datetime)
    : null

  const ensureUtcDefaultDateTime = () => {
    if (selectedDateTime) return
    const now = new Date()
    const utcNow = new Date(now.getTime() + now.getTimezoneOffset() * 60000)
    setSelectedDateTime(utcNow.toISOString().slice(0, 16))
  }

  const isV1Contract = project?.contractVersion === 'v1'
  const editDealUrl = `/deals/${project.id}/edit`
  const editSummaryUrl = `/deals/${project.id}/edit-summary`

  // const handleCopyLink = async () => {
  //   try {
  //     await navigator.clipboard.writeText(
  //       mockDealData.curatorInfo.promoShortLink,
  //     )
  //     setCopiedLink(true)
  //     setTimeout(() => setCopiedLink(false), 2000)
  //   } catch (err) {
  //     console.error('Failed to copy link:', err)
  //   }
  // }

  // const handleCopySale = async () => {
  //   try {
  //     await navigator.clipboard.writeText(mockDealData.liveDealPageUrl)
  //     setCopiedSale(true)
  //     setTimeout(() => setCopiedSale(false), 2000)
  //   } catch (err) {
  //     console.error('Failed to copy sale link:', err)
  //   }
  // }

  // const handleCopyAnalytics = async () => {
  //   try {
  //     await navigator.clipboard.writeText(
  //       mockDealData.curatorInfo.duneAnalyticsUrl,
  //     )
  //     setCopiedAnalytics(true)
  //     setTimeout(() => setCopiedAnalytics(false), 2000)
  //   } catch (err) {
  //     console.error('Failed to copy analytics link:', err)
  //   }
  // }

  // const handleDownloadForm = () => {
  //   const formData = {
  //     projectName: project.projectName,
  //     tagline: project.tagline,
  //     sector: project.sector,
  //     jurisdiction: project.jurisdiction,
  //     salesType: project.salesType,
  //     chain: project.chain,
  //     tokenInfo: project.tokenInfo,
  //     launchTimeline: project.launchTimeline,
  //     capTableSummary: project.capTableSummary,
  //     projectUrls: project.projectUrls,
  //     submittedAt: project.submittedAt,
  //     status: project.status,
  //   }

  //   const dataStr = JSON.stringify(formData, null, 2)
  //   const dataUri =
  //     'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr)

  //   const exportFileDefaultName = `${project.projectName
  //     .toLowerCase()
  //     .replace(/\s+/g, '-')}-submission-form.json`

  //   const linkElement = document.createElement('a')
  //   linkElement.setAttribute('href', dataUri)
  //   linkElement.setAttribute('download', exportFileDefaultName)
  //   linkElement.click()
  // }

  const handleAddTgeDate = () => {
    if (!selectedDateTime) {
      setLocalTgeError('Please select a date and time.')
      return
    }

    const isoInput = `${selectedDateTime}:00Z`
    const parsedDate = new Date(isoInput)
    if (Number.isNaN(parsedDate.getTime())) {
      setLocalTgeError('Invalid date. Please try again.')
      return
    }

    const now = new Date()
    if (parsedDate < now) {
      setLocalTgeError('TGE date cannot be in the past.')
      return
    }

    if (depositEndDate && parsedDate <= depositEndDate) {
      setLocalTgeError('TGE date must be after the deposit end date.')
      return
    }

    setLocalTgeError(null)
    onTgeDateUpdate?.(isoInput)
  }

  const formatDate = (dateString: string) => {
    // Handle null or undefined dateString gracefully
    if (!dateString) return 'TBD'
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    } catch (error) {
      console.error('Error formatting date:', error)
      return 'Invalid Date'
    }
  }

  // const getSecondaryMarketValue = () => {
  //   const status = project.status
  //   const tradingRoyalty = 0.015 // 1.5%

  //   if (status === 'Upcoming') {
  //     // No secondary market activity yet
  //     return {
  //       value: '$0',
  //       description: 'No secondary market activity yet',
  //     }
  //   } else if (status === 'Live') {
  //     // Estimate based on current raise and assumed trading volume (10% of raise)
  //     const estimatedVolume = project.totalRaised * 0.1
  //     const estimatedRoyalty = estimatedVolume * tradingRoyalty
  //     return {
  //       value: `$${estimatedRoyalty.toLocaleString(undefined, {
  //         maximumFractionDigits: 0,
  //       })}`,
  //       description: 'Estimated based on current trading volumes',
  //     }
  //   } else if (status === 'Completed') {
  //     // Calculate based on total raise and assumed trading volume (25% of total raise)
  //     const estimatedVolume = project.totalRaised * 0.25
  //     const estimatedRoyalty = estimatedVolume * tradingRoyalty
  //     return {
  //       value: `$${estimatedRoyalty.toLocaleString(undefined, {
  //         maximumFractionDigits: 0,
  //       })}`,
  //       description: 'Based on accumulated secondary market volumes',
  //     }
  //   }

  //   return {
  //     value: '$0',
  //     description: 'No data available',
  //   }
  // }

  const handleExportCsv = () => {
    if (participantsList.length === 0) return
    const headers = [
      'Legal Name',
      'Privy ID',
      'Registered Address',
      'Wallet Address',
      'Email',
    ]
    const rows = participantsList.map((p) => [
      p.legalName,
      p.privyId,
      p.registeredAddress,
      p.walletAddress,
      p.email,
    ])
    const csvContent = [
      headers.map((h) => `"${h}"`).join(','),
      ...rows.map((r) => r.map((v) => `"${String(v ?? '')}"`).join(',')),
    ].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `participants-${project.id}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  const progressPercentage = (project.totalRaised / project.maxRaise) * 100

  const timelineData = project.timelines

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-6xl mx-auto px-4 py-8 pt-2">
        <div className="space-y-6">
          {/* {shouldShowActionBanner && (
            <CarouselBanner
              type="Ending Soon"
              message={`Action required: TGE date is missing for ${mockDealData.projectName}. Please set the token generation event (TGE) date to continue.`}
              buttonText="Set TGE Date"
              buttonAction={() => setShowTgeDateModal(true)}
            />
          )} */}

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-neutral-100 flex-shrink-0">
                  <img
                    src={project.projectLogo || '/placeholder.svg'}
                    alt={project.projectName}
                    width={64}
                    height={64}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2 gap-4 flex-wrap">
                    <h1 className="font-bold text-neutral-900 text-2xl">
                      {project.projectName}
                    </h1>
                    <div className="flex items-center gap-2">
                      <Button
                        asChild
                        variant="outline"
                        size="sm"
                        className="border-brand-primary text-brand-primary hover:border-brand-hover hover:text-brand-hover"
                      >
                        <Link to={editSummaryUrl}>
                          Edit Overview &amp; Sale Details
                        </Link>
                      </Button>
                      {isEquityDeal(project) && (
                        <Button
                          asChild
                          variant="outline"
                          size="sm"
                          className="border-brand-primary text-brand-primary hover:border-brand-hover hover:text-brand-hover"
                        >
                          <Link to={`/deals/${project.id}/shortlist`}>
                            Shortlist
                          </Link>
                        </Button>
                      )}
                      {isV1Contract && (
                        <Button
                          asChild
                          variant="outline"
                          size="sm"
                          className="border-brand-primary text-brand-primary hover:border-brand-hover hover:text-brand-hover"
                        >
                          <Link to={editDealUrl}>Edit Deal</Link>
                        </Button>
                      )}
                      {!isCurator && (
                        <Button
                          asChild
                          variant="outline"
                          size="sm"
                          className="border-brand-primary text-brand-primary hover:border-brand-hover hover:text-brand-hover"
                        >
                          <Link to={`/deals/${project.id}/partner-program`}>
                            Partner Program
                          </Link>
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {project.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-block px-3 py-1 bg-neutral-100 border border-neutral-200 font-medium text-neutral-700 rounded-md text-xs"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* <div className="mb-4">
                <div className="flex flex-wrap gap-6 pt-2">
                  <div>
                    <div className="font-medium text-sm mb-1 text-neutral-600">
                      Live Deal Page
                    </div>
                    <a
                      href={project.liveDealPageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 hover:underline transition-colors text-sm flex items-center gap-1"
                    >
                      {project.liveDealPageUrl}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  <div>
                    <div className="font-medium text-sm mb-1 text-neutral-600">
                      Dune Analytics
                    </div>
                    <a
                      href={project.curatorInfo.duneAnalyticsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 hover:underline transition-colors text-sm flex items-center gap-1"
                    >
                      Track sale performance via Dune
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div> */}

              <div className="border-t border-neutral-200 mb-6"></div>

              <div className="flex flex-col lg:flex-row gap-6 items-stretch">
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-neutral-500 mb-1">
                    Deal Type
                  </h3>
                  <div className="bg-white rounded-lg p-4 py-0 px-0">
                    <div className="font-medium text-neutral-900 text-sm capitalize">
                      {project.saleType || '—'}
                    </div>
                  </div>
                </div>

                <div className="hidden lg:flex items-center">
                  <div className="w-px bg-neutral-200 h-16"></div>
                </div>

                <div className="flex-1">
                  <h3 className="text-sm font-medium mb-1 text-neutral-600">
                    TGE Date
                  </h3>
                  <div className="bg-white rounded-lg p-4 py-0 px-0">
                    {project.tgeDate ? (
                      <div className="font-medium text-neutral-900 text-sm flex items-center gap-2">
                        {formatDate(project.tgeDate)}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setShowTgeDateModal(true)}
                          className="flex items-center gap-2 hover:text-brand-hover transition-colors text-sm font-medium text-brand-primary"
                        >
                          <Plus className="w-4 h-4" />
                          Add TGE Date
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="hidden lg:flex items-center">
                  <div className="w-px bg-neutral-200 h-16"></div>
                </div>

                <div className="flex-1">
                  <h3 className="text-sm font-medium mb-1 text-neutral-600">
                    Chain
                  </h3>
                  <div className="bg-white rounded-lg p-4 py-0 px-0">
                    {chain ? (
                      <div className="font-medium text-neutral-900 text-sm flex items-center gap-2">
                        <img
                          src={chain.chainImage}
                          alt={chain.chainName}
                          width={16}
                          height={16}
                        />
                        {chain.chainName}
                      </div>
                    ) : (
                      <div className="font-medium text-neutral-500 text-sm">
                        Chain information unavailable
                      </div>
                    )}
                  </div>
                </div>

                <div className="hidden lg:flex items-center">
                  <div className="w-px bg-neutral-200 h-16"></div>
                </div>

                <div className="flex-[2]">
                  <h3 className="text-sm font-medium mb-1 text-neutral-600">
                    Sale Progress{' '}
                  </h3>
                  <div className="bg-white rounded-lg p-4 py-0 px-0">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-neutral-600">Total Raised</span>
                      <span className="font-medium text-neutral-900">
                        ${project.totalRaised.toLocaleString()}
                      </span>
                    </div>
                    <div className="w-full rounded-full mb-2 h-1.5 bg-neutral-100 relative overflow-hidden">
                      <div
                        className="rounded-full transition-all duration-300 bg-status-info h-1.5"
                        style={{
                          width: `${Math.min(progressPercentage, 100)}%`,
                        }}
                      ></div>
                    </div>
                    <div className="text-xs text-neutral-500">
                      Max: ${project.maxRaise.toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="hidden lg:flex items-center">
                  <div className="w-px bg-neutral-200 h-16"></div>
                </div>

                <div className="flex-1">
                  <h3 className="text-sm font-medium mb-1 text-neutral-600">
                    Participants
                  </h3>
                  <div className="bg-white rounded-lg p-4 py-0 px-0">
                    <div className="font-medium text-neutral-900 text-sm">
                      {project.participants.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <TimelineSection timeline={timelineData} />

          <Card>
            <CardContent className="p-6">
              <h3 className="font-semibold text-neutral-950 mb-4 text-lg">
                Fee Structure
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium mb-2 text-neutral-600">
                      Values Set (%)
                    </h4>
                    <div className="bg-neutral-50 rounded-lg p-3">
                      <div className="text-neutral-900 font-medium">
                        {project.platformFeePercent}%
                      </div>
                      <div className="text-xs text-neutral-600 mt-1">
                        Platform fee on successful deals
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium mb-2 text-neutral-600">
                      Value to be Claimed
                    </h4>
                    <div className="bg-neutral-50 rounded-lg p-3">
                      <div className="text-neutral-900 font-medium">
                        $
                        {(
                          (project.totalRaised * project.platformFeePercent) /
                          100
                        ).toLocaleString()}
                      </div>
                      <div className="text-xs text-neutral-600 mt-1">
                        Based on current raise amount
                      </div>
                    </div>
                  </div>
                </div>
                {/* <div className="pt-2 border-t border-neutral-200">
                  <h4 className="mb-3 text-neutral-950 text-base font-semibold pt-2">
                    Secondary Market
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h5 className="text-sm font-medium mb-2 text-neutral-600">
                        Trading Royalty (%)
                      </h5>
                      <div className="bg-neutral-50 rounded-lg p-3">
                        <div className="text-neutral-900 font-medium">1.5%</div>
                        <div className="text-xs text-neutral-600 mt-1">
                          Trading fee on each secondary market transaction
                        </div>
                      </div>
                    </div>
                    <div>
                      <h5 className="text-sm font-medium mb-2 text-neutral-600">
                        Value to be Claimed
                      </h5>
                      <div className="bg-neutral-50 rounded-lg p-3">
                        <div className="text-neutral-900 font-medium">
                          {secondaryMarketData.value}
                        </div>
                        <div className="text-xs text-neutral-600 mt-1">
                          {secondaryMarketData.description}
                        </div>
                      </div>
                    </div>
                  </div>
                </div> */}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <h3 className="font-medium text-neutral-900 mb-3">
                Social Links
              </h3>
              <div className="flex flex-wrap gap-3">
                {project.projectUrls.website && (
                  <a
                    href={project.projectUrls.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 bg-white border border-neutral-200 hover:bg-neutral-50 transition-colors rounded-md"
                  >
                    <Globe className="w-4 h-4 text-neutral-600" />
                    <span className="font-medium text-neutral-700 text-xs">
                      Website
                    </span>
                  </a>
                )}
                {project.projectUrls.x && (
                  <a
                    href={project.projectUrls.x}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 bg-white border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors"
                  >
                    <img
                      src="/icons/x-twitter.svg"
                      alt="X (Twitter)"
                      width={16}
                      height={16}
                    />
                    <span className="font-medium text-neutral-700 text-xs">
                      Twitter
                    </span>
                  </a>
                )}
                {project.projectUrls.discord && (
                  <a
                    href={project.projectUrls.discord}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 bg-white border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors"
                  >
                    <img
                      src="/icons/discord.svg"
                      alt="Discord"
                      width={16}
                      height={16}
                    />
                    <span className="font-medium text-neutral-700 text-xs">
                      Discord
                    </span>
                  </a>
                )}
                {project.projectUrls.telegram && (
                  <a
                    href={project.projectUrls.telegram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 bg-white border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors"
                  >
                    <img
                      src="/icons/telegram.svg"
                      alt="Telegram"
                      width={16}
                      height={16}
                    />
                    <span className="font-medium text-neutral-700 text-xs">
                      Telegram
                    </span>
                  </a>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Participants Section */}
          <Card>
            <button
              type="button"
              onClick={() => setParticipantsOpen(!participantsOpen)}
              className="w-full flex items-center justify-between p-6 text-left"
            >
              <h3 className="text-lg font-semibold text-neutral-950">
                {`Participants (${
                  isLoadingEquitySubmissions ? '…' : participantsList.length
                })`}
              </h3>
              <div className="flex items-center gap-2">
                {participantsOpen && participantsList.length > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleExportCsv()
                    }}
                    className="flex items-center gap-2 text-neutral-900"
                  >
                    Export CSV
                  </Button>
                )}
                <ChevronDown
                  className={cn(
                    'w-5 h-5 text-neutral-500 transition-transform',
                    participantsOpen && 'rotate-180',
                  )}
                />
              </div>
            </button>

            {participantsOpen && (
              <CardContent className="pt-0">
                {equitySubmissionsError && (
                  <p className="text-sm text-red-600 px-4 pb-2">
                    Could not load participants. Check the network or API path.
                  </p>
                )}
                {isLoadingEquitySubmissions && (
                  <p className="text-sm text-neutral-500 px-4 py-6">
                    Loading participants…
                  </p>
                )}
                {!isLoadingEquitySubmissions &&
                  !equitySubmissionsError &&
                  participantsList.length === 0 && (
                    <p className="text-sm text-neutral-500 px-4 py-6">
                      No participant submissions yet.
                    </p>
                  )}
                {!isLoadingEquitySubmissions &&
                  !equitySubmissionsError &&
                  participantsList.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-neutral-200">
                            <th className="text-left py-3 px-4 font-medium text-neutral-600 min-w-[160px]">
                              Legal Name
                            </th>
                            <th className="text-left py-3 px-4 font-medium text-neutral-600 min-w-[260px]">
                              Privy ID
                            </th>
                            <th className="text-left py-3 px-4 font-medium text-neutral-600 min-w-[200px]">
                              Registered Address
                            </th>
                            <th className="text-left py-3 px-4 font-medium text-neutral-600 min-w-[160px]">
                              Wallet Address
                            </th>
                            <th className="text-left py-3 px-4 font-medium text-neutral-600 min-w-[180px]">
                              Email
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {participantsList.map((participant) => (
                            <tr
                              key={participant.id}
                              className="border-b border-neutral-100 last:border-0"
                            >
                              <td className="py-3 px-4 align-top">
                                <div className="font-medium text-neutral-900">
                                  {participant.legalName}
                                </div>
                              </td>
                              <td className="py-3 px-4 align-top">
                                <span className="font-mono text-xs text-neutral-500 break-all">
                                  {participant.privyId}
                                </span>
                              </td>
                              <td className="py-3 px-4 align-top text-neutral-700">
                                {participant.registeredAddress}
                              </td>
                              <td className="py-3 px-4 align-top">
                                <span
                                  className="font-mono text-xs text-neutral-700 break-all"
                                  title={participant.walletAddress}
                                >
                                  {participant.walletAddress.slice(0, 6)}...
                                  {participant.walletAddress.slice(-4)}
                                </span>
                              </td>
                              <td className="py-3 px-4 align-top">
                                <span className="text-sm text-neutral-700">
                                  {participant.email}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
              </CardContent>
            )}
          </Card>

          {showFullSubmission && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-neutral-900">
                      Full Submission Details
                    </h2>
                    <button
                      onClick={() => setShowFullSubmission(false)}
                      className="w-8 h-8 flex items-center justify-center text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 rounded-full transition-colors"
                    >
                      ×
                    </button>
                  </div>
                  <div className="space-y-4 text-sm">
                    <div>
                      <h3 className="font-medium text-neutral-900 mb-2">
                        Project Information
                      </h3>
                      <div className="space-y-1 text-neutral-700">
                        <p>
                          <strong>Name:</strong> {project.projectName}
                        </p>
                        <p>
                          <strong>Tagline:</strong> {project.tagline}
                        </p>
                        <p>
                          <strong>Sector:</strong> {project.sector}
                        </p>
                        <p>
                          <strong>Jurisdiction:</strong> {project.jurisdiction}
                        </p>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-medium text-neutral-900 mb-2">
                        Deal Details
                      </h3>
                      <div className="space-y-1 text-neutral-700">
                        <p>
                          <strong>Deal Type:</strong>{' '}
                          {(project as any).saleType || '—'}
                        </p>
                        <p>
                          <strong>Chain:</strong> {project.chainName}
                        </p>
                        <p>
                          <strong>Token:</strong> {project.tokenInfo.ticker}
                        </p>
                        <p>
                          <strong>Token Price:</strong>{' '}
                          {project.tokenInfo.tokenPrice}
                        </p>
                        <p>
                          <strong>Implied FDV:</strong>{' '}
                          {project.tokenInfo.impliedFDV}
                        </p>
                        <p>
                          <strong>Cap Table:</strong> {project.capTableSummary}
                        </p>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-medium text-neutral-900 mb-2">
                        Timeline
                      </h3>
                      <div className="space-y-1 text-neutral-700">
                        <p>
                          <strong>Deposit Start:</strong>{' '}
                          {formatDate(project.launchTimeline.depositStart)}
                        </p>
                        <p>
                          <strong>Sale Start:</strong>{' '}
                          {formatDate(project.launchTimeline.saleStart)}
                        </p>
                        <p>
                          <strong>Claim Date:</strong>{' '}
                          {formatDate(project.launchTimeline.claim)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <Dialog open={showTgeDateModal} onOpenChange={setShowTgeDateModal}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-xl font-bold text-neutral-950">
                  Add TGE Date
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Date & Time (UTC)
                  </label>
                  <input
                    type="datetime-local"
                    value={selectedDateTime}
                    onFocus={ensureUtcDefaultDateTime}
                    onChange={(e) => {
                      setSelectedDateTime(e.target.value)
                      if (localTgeError) setLocalTgeError(null)
                    }}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md text-neutral-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {localTgeError && (
                    <p className="text-status-error text-xs mt-1">
                      {localTgeError}
                    </p>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setShowTgeDateModal(false)}
                  className="flex-1 text-neutral-900 border-neutral-300 hover:bg-neutral-100"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddTgeDate}
                  disabled={!selectedDateTime || isSavingTge}
                  className="flex-1 bg-brand-primary text-white hover:bg-brand-hover disabled:opacity-50"
                >
                  {isSavingTge ? 'Saving...' : 'Add Date'}
                </Button>
              </DialogFooter>
              {tgeError && (
                <p className="text-status-error text-sm mt-3">{tgeError}</p>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  )
}
